/**
 * Task JJ: several columns may be mapped to the same field ("可以多选择").
 *  - text fields (备注 / 标签 / 地址 / 配套列表 …) combine the values of all their columns;
 *  - single-value fields (名 / 手机号 / 邮箱 / 生日 / 会员号 …) take the first non-empty value (left to right) — or, when the
 *    file holds side-by-side repeated groups (Name / Phone / Name (2) / Phone (2)), each group becomes its own record;
 *  - the helper columns of our own error report (Row / Problem) are recognised and never imported.
 * Pure functions: used by the API (auto mapping, dry run) and by the web mapping screen (picker sections, notes).
 */
import { autoMapHeaders } from "./import-fields.js";
import type { ImportKind } from "./import-kinds.js";

export const FIELD_GROUP_KEYS = ["basic", "contact", "member", "packages", "other"] as const;
export type FieldGroupKey = (typeof FIELD_GROUP_KEYS)[number];
const CUSTOMER_GROUPS: Record<FieldGroupKey, string[]> = {
  basic: ["name", "first_name", "last_name", "gender", "birthday", "address"],
  contact: ["phone", "email", "marketing_consent", "marketing_email_consent", "notify_consent"],
  member: ["member_no", "tier", "tags", "points", "last_visit", "total_spent"],
  packages: ["credit_principal", "credit_bonus", "package_name", "package_sessions", "package_value", "package_expiry", "packages"],
  other: ["notes"],
};
const OTHER_FIELDS = new Set(["description", "notes", "online", "bookable", "reorder_point"]);
const CONTACT_FIELDS = new Set(["phone", "email", "customer_phone"]);
/** the picker's sections (基本资料 / 联系 / 会员 / 配套储值 / 其他); empty sections are left out */
export function fieldGroups(kind: ImportKind, fields: readonly string[]): Array<{ key: FieldGroupKey; fields: string[] }> {
  const of = (f: string): FieldGroupKey => {
    if (kind === "customers") return FIELD_GROUP_KEYS.find((k) => CUSTOMER_GROUPS[k].includes(f)) ?? "other";
    return CONTACT_FIELDS.has(f) ? "contact" : OTHER_FIELDS.has(f) ? "other" : "basic";
  };
  return FIELD_GROUP_KEYS.map((key) => ({ key, fields: fields.filter((f) => of(f) === key) })).filter((g) => g.fields.length > 0);
}

/** fields whose values are combined when several columns use them (separator); every other field is single-value */
const COMBINE: Record<ImportKind, Record<string, string>> = {
  customers: { tags: ", ", notes: " / ", address: ", ", packages: "; " },
  services: { description: " / " },
  products: {},
  staff: {},
  appointments: { notes: " / " },
};
export const combineSeparator = (kind: ImportKind, field: string): string | null => COMBINE[kind]?.[field] ?? null;
export const isCombineField = (kind: ImportKind, field: string) => combineSeparator(kind, field) !== null;

/**
 * The value of one field from all its columns (in column order): combined for text fields, otherwise the first
 * non-empty value; conflict = several different non-empty values for a single-value field (shown as a note).
 */
export function pickFieldValue(values: string[], separator: string | null): { value: string; conflict: boolean } {
  const filled = values.map((v) => (v ?? "").trim()).filter(Boolean);
  if (!filled.length) return { value: "", conflict: false };
  if (separator !== null) {
    const seen = new Set<string>(), out: string[] = [];
    for (const v of filled) { const k = v.toLowerCase(); if (!seen.has(k)) { seen.add(k); out.push(v); } }
    return { value: out.join(separator), conflict: false };
  }
  const first = filled[0]!;
  const squash = (s: string) => s.toLowerCase().replace(/\s+/g, "");
  return { value: first, conflict: filled.some((v) => squash(v) !== squash(first)) };
}

/** column indexes per field (several columns per field allowed), in column order */
export function columnsByField(headers: string[], mapping: Record<string, string>): Map<string, number[]> {
  const out = new Map<string, number[]>();
  headers.forEach((h, i) => { const f = mapping[h]; if (f) out.set(f, [...(out.get(f) ?? []), i]); });
  return out;
}

// ---------------------------------------------------------------- our own error report re-uploaded
const ROW_HEADERS = new Set(["row", "行", "行号", "baris"]);
const PROBLEM_HEADERS = new Set(["problem", "problems", "问题", "masalah"]);
/** "Row" + "Problem" as the first two columns = the error report Senvia gave out → those 2 columns are never imported */
export function errorReportColumns(headers: string[]): number[] {
  const h0 = (headers[0] ?? "").trim().toLowerCase(), h1 = (headers[1] ?? "").trim().toLowerCase();
  return ROW_HEADERS.has(h0) && PROBLEM_HEADERS.has(h1) ? [0, 1] : [];
}

// ---------------------------------------------------------------- side-by-side repeated groups
const SUFFIX = /^(.*?\S)\s*(?:\((\d{1,2})\)|（(\d{1,2})）|[\s_#\-]+(\d{1,2})|(?<=[A-Za-z\u3400-\u9fff])(\d{1,2}))$/;
const PLACEHOLDER = /^column \d+( \(\d+\))?$/i;
export interface RepeatedGroups { count: number; groups: number[][]; bases: string[] }

/**
 * "Name, Phone, Group, Name (2), Phone (2), Group (2)" (duplicate headers get " (2)" when the file is read), "Name 2",
 * "Name_2", "姓名2" … → 2 groups of columns describing 2 records in one row. Needs at least 2 repeated headers, one of
 * them a name; every group has the same columns in the same order. Columns outside the groups apply to every group.
 */
export function detectRepeatedGroups(headers: string[], kind: ImportKind = "customers"): RepeatedGroups | null {
  const byBase = new Map<string, Array<{ index: number; n: number }>>();
  const order: string[] = [];
  headers.forEach((raw, index) => {
    const h = raw.normalize("NFKC").replace(/\s+/g, " ").trim();
    if (!h || PLACEHOLDER.test(h)) return;
    const m = SUFFIX.exec(h);
    const base = (m ? m[1]! : h).toLowerCase();
    const n = m ? Number(m[2] ?? m[3] ?? m[4] ?? m[5]) : 1;
    if (!byBase.has(base)) { byBase.set(base, []); order.push(base); }
    byBase.get(base)!.push({ index, n });
  });
  const repeated = order.filter((b) => {
    const list = byBase.get(b)!;
    return list.length >= 2 && list.length <= 10 && new Set(list.map((x) => x.n)).size === list.length;
  });
  if (repeated.length < 2) return null;
  // the most common number of repeats wins (e.g. 2 groups)
  const tally = new Map<number, number>();
  for (const b of repeated) tally.set(byBase.get(b)!.length, (tally.get(byBase.get(b)!.length) ?? 0) + 1);
  const count = [...tally.entries()].sort((a, b) => b[1] - a[1] || b[0] - a[0])[0]![0];
  const bases = repeated.filter((b) => byBase.get(b)!.length === count);
  if (bases.length < 2) return null;
  const nameFields = kind === "customers" ? ["name", "first_name", "last_name"] : kind === "appointments" ? ["customer_name"] : ["name"];
  const mapped = kind === "customers" ? autoMapHeaders(bases) : null;
  const hasName = bases.some((b) => (mapped ? nameFields.includes(mapped[b] ?? "") : /name|姓名|名字|nama/.test(b)));
  if (!hasName) return null;
  const sorted = bases.map((b) => [...byBase.get(b)!].sort((x, y) => x.n - y.n));
  const groups = Array.from({ length: count }, (_v, g) => sorted.map((list) => list[g]!.index));
  return { count, groups, bases };
}

/** valid "split into records" choice: 2..10 groups, same size, distinct columns inside the header range */
export function validGroups(groups: unknown, width: number): groups is number[][] {
  if (!Array.isArray(groups) || groups.length < 2 || groups.length > 10) return false;
  const size = Array.isArray(groups[0]) ? groups[0].length : 0;
  const seen = new Set<number>();
  for (const g of groups) {
    if (!Array.isArray(g) || g.length !== size || size < 1) return false;
    for (const c of g) { if (!Number.isInteger(c) || c < 0 || c >= width || seen.has(c)) return false; seen.add(c); }
  }
  return true;
}

/**
 * The mapping that applies to group g (1-based) of a split row: columns of the other groups are ignored; a column of
 * this group without a field uses the field of the same position in another group (each group maps to the same fields).
 */
export function groupMapping(headers: string[], mapping: Record<string, string>, groups: number[][] | null | undefined, g: number | null | undefined): Record<string, string> {
  if (!groups || groups.length < 2 || !g) return mapping;
  const out: Record<string, string> = { ...mapping };
  const pos = new Map<number, [number, number]>();
  groups.forEach((cols, gi) => cols.forEach((c, k) => pos.set(c, [gi, k])));
  headers.forEach((h, i) => {
    const p = pos.get(i);
    if (!p) return;
    if (p[0] !== g - 1) { out[h] = ""; return; }
    if (out[h]) return;
    for (const cols of groups) { const c = cols[p[1]]; const f = c === undefined ? "" : mapping[headers[c]!] ?? ""; if (f) { out[h] = f; break; } }
  });
  return out;
}

/** "12" or, for a split row, "12（第 2 组）" / "12 (set 2)" */
export function importRowLabel(row: number, group: number | null | undefined, lang: string): string {
  if (!group) return String(row);
  return lang === "zh" ? row + "（第 " + group + " 组）" : row + " (set " + group + ")";
}
